import React, { useCallback, useState, useEffect } from 'react';
import { PDFDocumentProxy } from 'pdfjs-dist/webpack';
import useGlobal from '../../store/Store';
import SteppedLineTo from 'react-lineto';
import { getWord } from '../../lib/api/aws-api';
import { generateUUID } from '../../lib/util';
import { getLabelStatus } from '../../lib/util/status';
import { getPageFromElement } from '../../lib/pdfjs-dom';
import { Events, scroller, animateScroll as scroll } from 'react-scroll';
import api from '../../config/AxiosService';
import PdfView from './PdfView';
import { PdfFile } from '../../types';
import { Label } from '../../types/label';
import { Position } from '../../types/result';
import { Spinner } from '../../components/loading/Spinner';
import { Icon } from '@ant-design/compatible';
import { Document } from 'react-pdf';

type Props = {
  newLabelAllowed?: boolean;
  showNonExisting?: boolean;
  file?: null | PdfFile;
  labels: Label[];
  onAnnotationChange: (labels: Label[]) => any;
  withCorrections?: null | boolean;
  publicDemo?: boolean;
};

/**
 * How this component works:
 *
 * in a previous version, the pdfDocument was set by the pdfjs.getDocument function
 *  -> this resulted in missing text in some pdf-files
 *
 * the fix: the pdfDocument is set by the Document component (from the react-pdf library)
 *  -> once this is loaded, it is then passed to the PdfView, where it is shown by the
 *     pdfjs library.
 *  -> using the react-pdf library gives us a better pdfDocument file, where all text is visible
 *  => this is obviously a workaround, but it is more cost-effective than re-writing all the pdf,
 *     pdfResultLayer, pdfWordsLayer and connecting lines logic to the other library.
 *
 */

export const PdfContainer = (props: Props) => {
  const { labels, newLabelAllowed, onAnnotationChange, file, showNonExisting, withCorrections, publicDemo } = props;

  const [lineKey, setLineKey] = useState<string>(generateUUID());

  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);

  const [scrolling, setScrolling] = useState<boolean>(false);
  const [globalState, globalActions] = useGlobal();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    !globalState.labelEdit && globalState.labelSelected && scrollToWord(globalState.labelSelected);
  }, [globalState.labelSelected, globalState.labelEdit]);

  useEffect(() => {
    setPdfDocument(null);
    setLoading(true);
  }, [file]);

  const getWordsContent = async (file: PdfFile) => {
    const { data } = await api.get(file.presignedWordsUrl);
    return data.content;
  };

  const scrollToWord = (label: Label) => {
    const element = document.getElementById(label.id) || document.getElementById(label.id + '1');
    if (!element) {
      console.log('Element not found:' + label.id);
      return;
    }
    const page = getPageFromElement(element);
    if (!page) {
      console.log('Page not found for element', element);
      return;
    }

    setScrolling(true);
    const pageTop = page.node.offsetTop;
    const pageLeft = page.node.offsetLeft;

    const goToContainer = new Promise<void>(resolve => {
      Events.scrollEvent.register('end', () => {
        resolve();
        Events.scrollEvent.remove('end');
      });

      scroller.scrollTo('scroll-container', undefined);
    });

    goToContainer.then(() => {
      scroll.scrollTo(pageLeft + element.offsetLeft - 200, {
        horizontal: true,
        duration: 300,
        delay: 0,
        smooth: 'easeInOutQuart',
        containerId: 'scroll-container',
      });
      scroll.scrollTo(pageTop + element.offsetTop - 200, {
        duration: 300,
        delay: 0,
        smooth: 'easeInOutQuart',
        containerId: 'scroll-container',
      });
      setTimeout(() => setScrolling(false), 300); // prevent overflow from LineTo being out of view
    });
  };

  const refreshLine = useCallback(() => {
    setLineKey(generateUUID());
  }, [setLineKey]);

  const onAddLabel = async (position: Position, page: { number: number }, name: string) => {
    if (file) {
      const selectableLabel = globalState.labelOptions.find(l => l.name === name);
      const word = await getWord(file.id, page.number, position, file.originalType === 'DatasetFile');
      const label = {
        id: generateUUID(),
        name: name,
        source: 'manual',
        page: page.number,
        word: word,
        location: position,
        exists: true,
        reliable: true,
        color: selectableLabel?.color,
      };

      const newLabels = globalActions.addLabel(label);
      onAnnotationChange && onAnnotationChange(newLabels);

      return label;
    }
  };

  const cancelEdit = () => {
    globalActions.setLabelEdit(null);
  };

  const onEditLabel = async (position: Position, page: { number: number }) => {
    if (withCorrections) return;

    if (file) {
      const word = await getWord(file.id, page.number, position, file.originalType === 'DatasetFile');
      const label = {
        ...globalState.labelEdit,
        source: 'manual',
        page: page.number,
        word: word,
        location: position,
        exists: true,
        reliable: true,
      };

      const newLabels = globalActions.editLabel(label);
      onAnnotationChange && onAnnotationChange(newLabels);
      cancelEdit();
    }
  };

  const onEditThisLabel = async (label: Label, newPosition: Position, page: { number: number }) => {
    if (withCorrections) return;

    if (file) {
      const word = await getWord(file.id, page.number, newPosition, file.originalType === 'DatasetFile');
      const newLabel = {
        ...label,
        source: 'manual',
        page: page.number,
        word: word,
        location: newPosition,
        exists: true,
        reliable: true,
      };

      const newLabels = globalActions.editLabel(newLabel);
      onAnnotationChange && onAnnotationChange(newLabels);
      cancelEdit();
    }
  };

  const onRemoveLabel = async (label: Label) => {
    const newLabels = globalActions.removeLabel(label);
    onAnnotationChange && onAnnotationChange(newLabels);
  };

  const onReEditLabel = async (label: Label) => {
    const newLabels = globalActions.editLabel(label);
    onAnnotationChange && onAnnotationChange(newLabels);
  };

  const onResetRemovedLabel = async (label: Label) => {
    const newLabels = globalActions.addLabel(label);
    onAnnotationChange && onAnnotationChange(newLabels);
  };

  const onScroll = useCallback(() => {
    refreshLine();
  }, [refreshLine]);

  useEffect(() => {
    return () => {
      setPdfDocument(null);
    };
  }, []);

  return (
    <div className="pdf-content" style={{ width: '100%' }}>
      <div
        style={{
          width: '100%',
          height: window.innerHeight - 60 + 'px',
          overflow: 'hidden',
          backgroundColor: '#f7f7f7',
          position: 'relative',
        }}>
        {/* This document component does not show the pdf-file, only needed to setPdfDocument */}
        {file && (
          <Document
            loading={<></>}
            file={file?.presignedUrl}
            onLoadSuccess={pdf => {
              console.log('setting pdf document');
              setPdfDocument(pdf as any);
              setLoading(false);
            }}></Document>
        )}
        {loading && <Spinner />}
        {!loading && file && pdfDocument && globalState.labels && (
          <PdfView
            pdfDocument={pdfDocument}
            getWordsContent={getWordsContent}
            wordsToHighlight={withCorrections ? file.resultFields : labels}
            selectedWord={globalState.labelSelected}
            editWord={globalState.labelEdit}
            readOnly={globalState.readOnly}
            showNonExisting={showNonExisting}
            newLabelAllowed={newLabelAllowed}
            onAddLabel={onAddLabel}
            onEditLabel={onEditLabel}
            onRemoveLabel={onRemoveLabel}
            onReEditLabel={onReEditLabel}
            onResetRemovedLabel={onResetRemovedLabel}
            onEditThisLabel={onEditThisLabel}
            onEditCancel={cancelEdit}
            onScroll={onScroll}
            withCorrections={withCorrections ? withCorrections : false}
            publicDemo={publicDemo}
          />
        )}
        {!loading && !pdfDocument && file && (
          <div className="h-full flex justify-center items-center">
            <div className="flex flex-col justify-center">
              <Icon className="text-xl" type="exclamation-circle" />
              <p className="text-lg text-center">We're sorry, this pdf was not found.</p>
            </div>
          </div>
        )}
        {!scrolling && globalState.labelSelected && (
          <>
            <SteppedLineTo
              key={lineKey}
              fromAnchor={'center right'}
              toAnchor={'center left'}
              from={globalState.labelSelected?.isTable && publicDemo ? 'connectLineB' : 'connectLineA'}
              to={globalState.labelSelected?.isTable && publicDemo ? 'connectLineA' : 'connectLineB'}
              delay={0}
              borderColor={
                publicDemo
                  ? '#00b9ff'
                  : globalState.labelSelected.color || getLabelStatus(globalState.labelSelected).color
              }
            />
            <SteppedLineTo
              key={lineKey + '1'}
              fromAnchor={'center right'}
              toAnchor={'center left'}
              from="connectLineC"
              to="connectLineD"
              delay={0}
              borderColor={'#52c41a'}
            />
          </>
        )}
      </div>
    </div>
  );
};
